import { TypedDocumentNode, gql } from '@apollo/client';

import { AveragePlayerScore } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { PlayerAppearance } from '@sorare/core/src/components/scoring/PlayerAppearance';
import { PlayerScore } from '@sorare/core/src/components/scoring/PlayerScore';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { TotalAveragePlayerScore } from '@sorare/core/src/lib/averagePlayerScore';
import { PlayerSearchViewAttributes } from '@sorare/core/src/lib/playerSearchView';

import { Cell } from 'components/benchObject/BenchObjectTable/ui';
import { useManagePlayerSearchView } from 'searchCommonPlayer/StatsManager/useManagePlayerSearchView';

import { AveragePlayerScoreCell_anyPlayer } from './__generated__/index.graphql';

type Props = {
  anyPlayer: AveragePlayerScoreCell_anyPlayer;
  mode: TotalAveragePlayerScore;
};
const isAppearanceMode = (
  mode: TotalAveragePlayerScore
): mode is
  | AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE
  | AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
  | AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE => {
  return [
    AveragePlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
    AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    AveragePlayerScore.LAST_FIVE_SO5_AVERAGE_SCORE,
  ].includes(mode);
};

export const AveragePlayerScoreCell = ({ mode, anyPlayer }: Props) => {
  const { up: laptopOrAbove } = useScreenSize('laptop');

  const { statsManager } = useManagePlayerSearchView();
  const showAppearances =
    statsManager[PlayerSearchViewAttributes.SHOW_APPEARANCES];

  const appearancesMode = isAppearanceMode(mode) ? mode : undefined;

  return (
    <Cell>
      <Vertical center gap={0.5}>
        <PlayerScore
          player={anyPlayer}
          mode={mode}
          small={!laptopOrAbove}
          hideTooltip
        />
        {appearancesMode && showAppearances && (
          <PlayerAppearance
            withTooltip
            player={anyPlayer}
            mode={appearancesMode}
          />
        )}
      </Vertical>
    </Cell>
  );
};

AveragePlayerScoreCell.fragments = {
  anyPlayer: gql`
    fragment AveragePlayerScoreCell_anyPlayer on AnyPlayerInterface {
      slug
      lastFifteenSo5Appearances
      lastFiveSo5Appearances
      lastTenSo5Appearances
      ...PlayerScore_anyPlayer
      ...PlayerAppearance_anyPlayer
    }
    ${PlayerScore.fragments.anyPlayer}
    ${PlayerAppearance.fragments.anyPlayer}
  ` as TypedDocumentNode<AveragePlayerScoreCell_anyPlayer>,
};
